<template>
  <div class="h-100">
    <div v-if="isLoading" class="h-100 d-flex align-center justify-center">
      <LoaderSpinner />
    </div>

    <div v-if="!isLoading" class="h-100 ma-0 pa-0 d-flex flex-column">
      <!-- Header -->
      <div
        class="d-flex justify-space-between align-center px-6"
        style="height: 56px;"
      >
        <div class="d-flex align-center">
          <div class="d-flex align-center">
            <v-icon :color="$constants.ICON_COLORS_BY_ENTITY.finding" size="24">
              {{ $constants.ICONS_BY_ENTITY.finding }}
            </v-icon>
            <p
              v-snip="1"
              class="pa-0 ma-0 ml-2 mr-4 blue-grey--text text--darken-3 body-1 font-weight-medium"
            >
              {{ $t('routines.finding_one') }}
            </p>
          </div>
          <FindingStatus
            :large="true"
            :status="finding.status"
            :canEdit="!hasRev && permissions.edit"
            @onChange="handleSelectStatus"
          />
        </div>

        <ChangedModalStatus
          v-if="showStatusModal"
          :placeholder="$t('common.cancel_finding')"
          :show="showStatusModal"
          :status="revstatusChanged"
          :subtitle="changedModalStatusText.subtitle"
          :title="changedModalStatusText.title"
          @updateModalStatus="handleChangeModalStatus"
          @hideModal="$emit('hideModal')"
        />
        <div class="d-flex justify-end align-center" v-if="!readOnly">
          <PlannerIntegrationBadge
            class="mr-4"
            :data="plannerIntegration"
            :tooltipMessage="$t('common.click_to_open_finding_in_ms_planner')"
          />
          <Button
            color="blueGrey"
            icon="mdi-trash-can-outline"
            :disabled="!permissions.delete"
            @click.stop="showDeleteDialog = true"
          />
          <Button
            color="blueGrey"
            icon="mdi-pencil-outline"
            :disabled="!permissions.edit"
            @click.stop="$emit('edit')"
          />
        </div>
      </div>

      <v-divider />
      <!-- Body -->
      <div style="height: calc(100vh - 56px - 84px); overflow-y: scroll;">
        <!-- Title & Link -->
        <div v-if="hasChangedStatusReason" class="px-6 mt-4 mb-1">
          <ChangedStatusAlert
            data-testid="findingDetails-changedStatusAlert-alert"
            :data="finding"
            :entity="'Finding'"
          />
        </div>
        <Alert
          v-if="showRedirectionAlert"
          class="mb-7 mx-6 mt-3"
          data-testid="findingDetails-alert-findingCanceled"
          :vuetifyProps="{
            value: showFindingInProcessAlert,
            color: 'blue',
            icon: 'mdi-progress-check',
          }"
          :buttonPlain="false"
          :buttonText="$t('common.view')"
          :text="$t('routines.finding_canceled')"
          @buttonClick="goToResolutionRev"
        />
        <Alert
          v-if="showRedirectionAlert && isCompleted && hasRev"
          class="mb-7 mx-6 mt-4"
          data-testid="findingDetails-alert-findingCompleted"
          :vuetifyProps="{
            value: true,
            color: 'green',
            icon: 'mdi-check-circle-outline',
          }"
          :buttonPlain="false"
          :text="$t('assignments.solved_finding_message')"
          :buttonText="$t('common.button.view_more')"
          @buttonClick="goToResolutionRev"
        />
        <p class="h6 blue-grey--text text--darken-4 px-6 mt-4 mb-4">
          {{ situation }}
        </p>

        <div class="px-6 py-0 mb-4 d-flex align-start">
          <Button
            color="primary"
            data-testid="findingDetails-button-goToRoutine"
            outlined
            size="small"
            :suppressActiveStyle="true"
            :text="$t('routines.go_to_routine')"
            :to="{
              name: 'routineExecutionDetail',
              params: { routineId: routine.id },
            }"
            :tooltip="executionName"
          />
        </div>

        <!-- Assignees & DueDate -->
        <div class="d-flex flex-column px-6 mb-4">
          <p class="grey--text text--darken-1 font-size-12 my-0 ">
            {{ $t('common.assignee_other') }}
          </p>
          <div>
            <Assignees :finding="finding" />
          </div>
        </div>

        <!-- Tags -->
        <div
          data-testid="findingDetails-section-tags"
          :class="[
            'd-flex',
            'flex-column',
            'mt-4',
            'px-6',
            hasTags ? 'mb-3' : 'mb-4',
          ]"
        >
          <p class="grey--text text--darken-1 font-size-12">
            {{ $t('common.tag_other') }}
          </p>
          <div class="d-flex flex-wrap">
            <FindingTagChips :tags="finding.tags || null" />
          </div>
        </div>

        <!-- Categories -->
        <div
          :class="[
            'd-flex',
            'flex-column',
            'mt-4',
            'px-6',
            hasCategories ? 'mb-3' : 'mb-4',
          ]"
        >
          <p class="grey--text text--darken-1 font-size-12 mt-0 mb-2 ">
            {{ $t('common.category_other') }}
          </p>
          <div v-if="hasCategories" class="d-flex flex-wrap">
            <v-chip
              v-for="category in finding.categories"
              data-private
              class="mb-1 mr-1"
              outlined
              small
              :key="category"
            >
              {{ $t(`common.categories.${category}`) }}
            </v-chip>
          </div>
          <div v-else>
            <v-chip color="grey--text text--darken-1" outlined small>
              {{ $t('routines.findings_no_categories') }}
            </v-chip>
          </div>
        </div>

        <!-- Situation Types -->
        <div
          data-testid="findingDetails-section-situationTypes"
          :class="[
            'd-flex',
            'flex-column',
            'mt-4',
            'px-6',
            hasSituationTypes ? 'mb-3' : 'mb-4',
          ]"
          v-if="!isImprovementIdeaType"
        >
          <p class="grey--text text--darken-1 font-size-12 ">
            {{ $t('settings.situation_types') }}
          </p>
          <div v-if="hasSituationTypes" class="d-flex flex-wrap">
            <v-chip
              v-for="situationType of situationTypes"
              class="mb-1 mr-1"
              outlined
              small
              :key="situationType.situation"
            >
              {{
                $t(
                  `quick_action.situation_types.situations.${situationType.situation}`
                )
              }}
            </v-chip>
          </div>
          <div v-else>
            <v-chip color="grey--text text--darken-1" outlined small>
              {{ $t('routines.findings_no_situation_types') }}
            </v-chip>
          </div>
        </div>

        <div class="d-flex px-6 my-4">
          <div class="d-flex flex-column mr-4">
            <p class="grey--text text--darken-1 font-size-12 mb-2">
              {{ $t('common.priority') }}
            </p>
            <Priority v-if="finding.priority" :priority="finding.priority" />
            <div v-else class="blue-grey--text text--darken-1">-</div>
          </div>
          <div class="d-flex flex-column">
            <p class="grey--text text--darken-1 font-size-12 mb-2">
              {{ $t('common.due_date') }}
            </p>
            <DueDate :dueDate="dueDate" />
          </div>
        </div>

        <!-- Evidence -->
        <div class="d-flex flex-column mt-4 mb-3 px-6">
          <p class="grey--text text--darken-1 font-size-12">
            {{ $t('assignments.solution_evidence_label') }}
          </p>
          <div v-if="findingMedia.length > 0" class="mb-4">
            <MediaContainer onlyThumbnails readOnly :images="findingMedia" />
          </div>
          <div v-else>
            <v-chip color="grey--text text--darken-1" outlined small>
              {{ $t('routines.findings_no_evidence') }}
            </v-chip>
          </div>
        </div>
      </div>

      <!-- Footer-->
      <div v-if="showActions" class="d-flex flex-column align-center px-4 py-2">
        <Button
          v-if="canComplete"
          color="primary"
          class="w-100"
          data-testid="findingDetails-button-complete"
          :disabled="isCanceled"
          :text="$t('assignments.finding_resolve_button')"
          @click="handleCompleteFinding"
        />
        <Button
          v-if="canCreateQuickAction"
          color="primary"
          class="w-100 mt-2"
          data-testid="findingDetails-button-createQuickAction"
          outlined
          :disabled="isCanceled"
          :text="
            $t('assignments.finding_follow_up_button', {
              revType: quickActionSingularLabel,
            })
          "
          @click="$emit('address', { addressMode: 'quickAction' })"
        />
        <Button
          v-if="canFollowUpAsImprovementIdea"
          class="w-100 mt-2"
          data-testid="findingDetails-button-followUpAsImprovementIdea"
          outlined
          :disabled="isCanceled"
          :text="
            $t('assignments.finding_follow_up_button', {
              revType: improvementIdeaSingularLabel,
            })
          "
          @click="handleFollowUpAsImprovementIdea"
        />
      </div>

      <DeleteConfirmationModal
        :show="showDeleteDialog"
        :isLoading="isDeleting"
        :title="$t('assignments.delete_title_one')"
        :text="deleteText"
        @cancel="showDeleteDialog = false"
        @delete="removeFinding"
      />
    </div>
  </div>
</template>

<script>
import { utils } from 'frontend-js-shared'
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import findingPermissions from '@/mixins/findingPermissions.vue'

import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import DueDate from './DueDate'
import Assignees from './Assignees'
import Alert from '@/components/Alert.vue'
import FindingTagChips from './FindingTagChips.vue'
import Priority from '@/components/Priority.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import Button from '@/components/UI/Button'
import FindingStatus from './FindingStatus.vue'
import ChangedModalStatus from '@/containers/Modals/ChangedModalStatus.vue'
import ChangedStatusAlert from '@/components/Core/ChangedStatusAlert.vue'
import PlannerIntegrationBadge from '@/components/PlannerIntegrationBadge.vue'

export default {
  name: 'FindingAddressDrawer',
  mixins: [mixin, userMixin, trackerMixin, findingPermissions],

  components: {
    Assignees,
    DueDate,
    MediaContainer,
    Alert,
    FindingTagChips,
    Priority,
    DeleteConfirmationModal,
    LoaderSpinner,
    Button,
    FindingStatus,
    ChangedModalStatus,
    ChangedStatusAlert,
    PlannerIntegrationBadge,
  },

  props: {
    finding: Object,
    routine: Object,
    routineDefinition: Object,
    readOnly: Boolean,
    showStatusModal: Boolean,
    showRedirectionAlert: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      improvementIdeaPascalCase: this.pascalCase(
        this.$constants.revTypes.improvementIdea
      ),
      quickActionPascalCase: this.pascalCase(
        this.$constants.revTypes.quickAction
      ),
      showDeleteDialog: false,
      isDeleting: false,
      isLoading: false,
      revstatusChanged: '',
      plannerIntegration: null,
    }
  },

  mounted() {
    if (this.isPlannerBadgesEnabled) {
      this.getFindingIntegrations()
    }
  },

  computed: {
    ...mapGetters(['account', 'organization']),

    isPlannerBadgesEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.plannerBadges)
    },

    isCompleted() {
      return this.finding?.status === 'completed'
    },

    dueDate() {
      return this.finding?.dueDate
    },

    situation() {
      return this.finding?.situation ?? ''
    },

    findingMedia() {
      return this.finding?.media ?? []
    },

    situationTypes() {
      return this.finding?.situationTypes ?? []
    },

    isImprovementIdeaType() {
      return this.finding?.revType === this.improvementIdeaPascalCase
    },

    hasTags() {
      return !_.isEmpty(this.finding.tags)
    },

    hasCategories() {
      return !_.isEmpty(this.finding.categories)
    },

    hasSituationTypes() {
      return (
        this.situationTypes.length > 0 &&
        this.finding.revType === this.quickActionPascalCase
      )
    },

    hasRev() {
      return Boolean(this.finding?.revId)
    },

    permissions() {
      return this.getPermissions({
        routine: this.routine,
        routineDefinition: this.routineDefinition,
        finding: this.finding,
      })
    },

    showActions() {
      return (
        !this.readOnly &&
        (this.canComplete ||
          this.canFollowUpAsImprovementIdea ||
          this.canCreateQuickAction)
      )
    },

    canComplete() {
      return this.permissions.address && !this.isCompleted && !this.hasRev
    },

    showFindingInProcessAlert() {
      return Boolean(this.finding?.revId && !this.isCompleted)
    },

    canFollowUpAsImprovementIdea() {
      return (
        this.permissions.address &&
        !this.isCompleted &&
        !this.hasRev &&
        this.organization?.featuresEnabled__improvementIdea
      )
    },

    canCreateQuickAction() {
      return (
        this.permissions.address &&
        !this.hasRev &&
        this.organization?.featuresEnabled__quickAction
      )
    },

    improvementIdeaSingularLabel() {
      return (
        this.organization?.customNamings?.improvementIdea?.singular ||
        this.$t('common.rev_type.improvement_idea_one')
      )
    },

    quickActionSingularLabel() {
      return (
        this.organization?.customNamings?.quickAction?.singular ||
        this.$t('common.rev_type.quick_action_one')
      )
    },

    executionName() {
      return this.routine?.name ?? ''
    },

    deleteText() {
      return this.finding?.revId
        ? this.$t('assignments.delete_completed_finding')
        : this.$t('assignments.delete_confirmation')
    },

    changedModalStatusText() {
      if (this.revstatusChanged == this.$constants.findings.status.canceled)
        return {
          title: this.$t('common.nice_try'),
          subtitle: this.$t('common.marked_finding_as_canceled'),
        }
      return {
        title: this.$t('common.need_a_break'),
        subtitle: this.$t('common.marked_finding_as_onHold'),
      }
    },

    hasChangedStatusReason() {
      if (this.finding?.status == this.$constants.findings.status.canceled)
        return Boolean(this.finding?.canceledReason)
      if (this.finding?.status == this.$constants.findings.status.onHold)
        return Boolean(this.finding?.onHoldReason)
      return false
    },

    isCanceled() {
      return Boolean(
        this.finding?.status == this.$constants.findings.status.canceled
      )
    },
  },

  methods: {
    async handleCompleteFinding() {
      try {
        await api.findings.update({
          organizationId: this.finding.organization,
          findingId: this.finding?.id,
          data: {
            status: 'completed',
          },
        })
        this.trackFindingStatusChanged(
          this.finding,
          'button',
          'completed',
          null
        )
        this.$emit('refresh')
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    onFindingCompleted({ revType, revId }) {
      this.navigateTo({
        name: _.camelCase(revType),
        params: {
          revId,
          organizationId: this.finding.organization,
          isFromFinding: true,
        },
      })
    },

    getAddressTrackingPayload(revType) {
      return {
        situation: this.finding?.situation ?? '',
        revType,
        hasMedia: Boolean(this.finding?.media?.length),
        hasAssignees: Boolean(this.finding?.assignees?.length),
        numberOfAssignees: this.finding?.assignees?.length ?? 0,
        hasDueDate: Boolean(this.finding?.dueDate),
      }
    },

    linkFindingWithRev(revId, revType) {
      return api.findings.update({
        organizationId: this.finding.organization,
        findingId: this.finding?.id,
        data: {
          revId,
          revType,
        },
      })
    },

    getInitialImprovementIdeaPayload() {
      return {
        name:
          this.finding?.situation?.substring?.(
            0,
            this.$constants.improvementIdea.titleMaxLength
          ) ?? '',
        owner: this.account?.id,
        organization: this.finding?.organization,
        site: this.finding?.site,
        tags: this.finding?.tags,
        collaborators: this.finding.assignees
          .filter(assignee => assignee?.id !== this.account?.id)
          .map(assignee => assignee?.id),
        dueDate: this.finding?.dueDate ?? null,
        cycle: {
          relateProblem: this.finding?.situation,
          relatePhotos: this.finding?.media?.map?.(media => media?.id),
        },
        categories: this.finding.categories,
        routineId: utils.getEntityId(this.routine),
        findingId: utils.getEntityId(this.finding),
      }
    },

    async createImprovementIdea() {
      const response = await api.rev.create(
        this.finding.organization,
        this.getInitialImprovementIdeaPayload()
      )

      return response.data
    },

    async handleFollowUpAsImprovementIdea() {
      try {
        const improvementIdea = await this.createImprovementIdea()

        await this.linkFindingWithRev(
          improvementIdea?.id,
          this.improvementIdeaPascalCase
        )
        this.trackAddressFinding(
          this.getAddressTrackingPayload(
            this.$constants.revTypes.improvementIdea
          )
        )
        await this.onFindingCompleted({
          revType: this.improvementIdeaPascalCase,
          revId: improvementIdea?.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    goToResolutionRev() {
      const revType =
        this.finding?.revType ?? this.$constants.revTypes.quickAction

      this.navigateTo({
        name: _.camelCase(revType),
        params: {
          revId: this.finding.revId,
          organizationId: this.finding.organization,
        },
      })
    },

    async removeFinding() {
      try {
        this.isDeleting = true
        await api.findings.delete(this.organizationId, this.finding.id)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
        this.showDeleteDialog = false
        this.$emit('deleted')
      }
    },
    handleSelectStatus(status) {
      this.$emit('updateFindingStatus', status)
      this.revstatusChanged = status
    },
    handleChangeModalStatus(payload) {
      this.$emit('changeModalStatus', payload)
    },

    async getFindingIntegrations() {
      const { data } = await api.integrations.getIntegrationsRelations({
        organizationId: this.finding?.organization,
        filters: {
          entityId: this.finding?.id,
          externalPlatformName: 'planner',
        },
      })

      this.plannerIntegration = data?.results?.[0] ?? null
    },
  },
}
</script>
